import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, FolderPlus, ExternalLink, Copy, CheckCircle2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// The single shared root under which every lead's sub-folder lives.
// "0_ODG_Product_Collateral" in Amanda's Drive.
export const CLOUD_ROOT_FOLDER_ID = "1YV6-MEy2y0z5TC0ufuseEoh_EtmkK7F9";
export const CLOUD_ROOT_URL = `https://drive.google.com/drive/folders/${CLOUD_ROOT_FOLDER_ID}`;

// Deterministic folder name convention: "<BusinessName>_<ContactFirst>_<ContactLast>"
// e.g. "Waldo Tire & Automotive" + "John Smith" → "Waldo_Tire_Automotive_John_Smith"
export function buildFolderName(businessName, contactName) {
  const clean = (s) =>
    String(s || "")
      .replace(/&/g, "and")
      .replace(/[^A-Za-z0-9\s_]/g, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join("_");
  const biz = clean(businessName) || "Lead";
  const ctc = clean(contactName);
  return ctc ? `${biz}_${ctc}` : biz;
}

/**
 * Compact inline control for the Strike List row.
 * - If lead has cloud_folder_id → "Open Folder" deep-link.
 * - Otherwise → "Initialize" opens the linking dialog.
 */
export function CloudFolderCell({ lead, onUpdated }) {
  if (lead.cloud_folder_id) {
    return (
      <a
        href={`https://drive.google.com/drive/folders/${lead.cloud_folder_id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-display text-emerald-400 hover:text-emerald-300 transition-colors group/link"
        title={lead.cloud_folder_name || "Open Cloud Folder"}
        data-testid={`cloud-folder-open-${lead.id}`}
      >
        <FolderOpen size={12} /> Open
        <ExternalLink size={10} className="opacity-70" />
      </a>
    );
  }
  return <InitializeCloudFolder lead={lead} onUpdated={onUpdated} />;
}

function InitializeCloudFolder({ lead, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [folderIdInput, setFolderIdInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const targetName = buildFolderName(lead.business_name, lead.contact_name);

  const copyName = async () => {
    try {
      await navigator.clipboard.writeText(targetName);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Folder name copied");
    } catch (_) {
      toast.error("Clipboard blocked — copy manually");
    }
  };

  const openRoot = () => {
    window.open(CLOUD_ROOT_URL, "_blank", "noopener,noreferrer");
  };

  const save = async () => {
    const raw = folderIdInput.trim();
    if (!raw) {
      toast.error("Paste the folder ID or URL first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/leader/leads/${lead.id}`, {
        cloud_folder_id: raw,
        cloud_folder_name: targetName,
      });
      toast.success("Cloud folder linked");
      setOpen(false);
      setFolderIdInput("");
      if (onUpdated && data?.lead) onUpdated(data.lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[11px] font-display text-slate-400 hover:text-gold transition-colors"
          data-testid={`cloud-folder-init-${lead.id}`}
        >
          <FolderPlus size={12} /> Initialize
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0A1122] border-white/10 text-white max-w-lg"
        data-testid="cloud-folder-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl flex items-center gap-3">
            <FolderPlus size={20} className="text-emerald-400" />
            Initialize Cloud Folder
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Create the sub-folder in Google Drive, then paste its URL or ID
            below so future clicks deep-link straight in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <div className="hud-label text-[10px] mb-2">Target Folder Name</div>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-emerald-300 text-xs font-mono-aviation break-all"
                data-testid="cloud-folder-target-name"
              >
                {targetName}
              </code>
              <button
                onClick={copyName}
                className="btn-ghost-gold px-3 py-2 rounded-sm text-xs inline-flex items-center gap-1.5"
                data-testid="cloud-folder-copy-name"
              >
                {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          <ol className="space-y-2 text-xs text-slate-300 list-decimal list-inside pl-1">
            <li>
              Open the root Drive folder and create a new sub-folder pasted
              from above.
            </li>
            <li>
              Open the new sub-folder in Drive and copy either the URL or the
              folder ID.
            </li>
            <li>Paste it below and save — future clicks deep-link straight in.</li>
          </ol>

          <div>
            <button
              onClick={openRoot}
              className="btn-ghost-gold w-full py-2.5 rounded-sm text-xs inline-flex items-center justify-center gap-2"
              data-testid="cloud-folder-open-root"
            >
              <ExternalLink size={13} /> Open Root Drive Folder
            </button>
          </div>

          <div>
            <div className="hud-label text-[10px] mb-2">Paste Folder URL or ID</div>
            <input
              value={folderIdInput}
              onChange={(e) => setFolderIdInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/… or bare ID"
              data-testid="cloud-folder-input"
              className="w-full bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={save}
              disabled={saving}
              data-testid="cloud-folder-save"
              className="btn-gold flex-1 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle2 size={14} />
              {saving ? "Saving..." : "Save & Link"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-sm text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
